import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import GetCookie from '../../utils/GetCookie';
import ThemeToggle from '../../globalComponents/ThemeToggle';
import Footer from '../../globalComponents/Footer';
import NotificationDropdown from '../../globalComponents/NotificationDropdown';
import AiSearchComponent, { SerachDisplayDiv } from '../../utils/AiSearch';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProfilePopup from '../../globalComponents/ProfilePopup';
import { ProfileCache, NavStateCache } from '../../globalComponents/NavigationCache';
import authNavigate from '../../globalComponents/AuthNav';
import store from '../../../store/store';
import { getBrowserTimezone, clearFormatterCache } from '../../utils/dateUtils';
import { useTimezone } from '../../utils/TimezoneContext';

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed_iam';

export const TopNav = () => {
  const accessToken = GetCookie('access_token');
  const navigate = useNavigate();
  const user_at = 'iam-console';
  const [response_ai, set_response_ai] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { timezone, setTimezone } = useTimezone();
  
  const [profileData, setProfileData] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const profileButtonRef = useRef(null);

  useEffect(() => {
    fetchProfileDetails();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileButtonRef.current && !profileButtonRef.current.contains(event.target)) {
        setShowProfilePopup(false);
      }
    };
    if (showProfilePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfilePopup]);

  const fetchProfileDetails = async () => {
    const cachedProfile = ProfileCache.get(user_at);
    if (cachedProfile) {
      setProfileData(cachedProfile);
      return;
    }
    try {
      setIsProfileLoading(true);
      const response = await axios.post(
        '/pceapi/general/profile-details',
        { console_type: user_at },
        { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
      );
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        navigate('/login');
        return;
      }
      
      if (data.status === 'failed') {
        setProfileError(data.message || 'Failed to load profile details');
        return;
      }
      
      if (data.status === 'success') {
        const fetchedTz = data.data.timezone;
        
        if (!fetchedTz) {
          const browserTz = getBrowserTimezone();
          data.data.timezone = browserTz;
          
          if (timezone !== browserTz) {
            clearFormatterCache();
            setTimezone(browserTz);
          }
          
          axios.post(
            '/pceapi/general/update-user-preferences',
            { timezone: browserTz, console_type: user_at },
            { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
          ).catch(err => console.error('Auto-save timezone failed:', err));
        } else if (fetchedTz !== timezone) {
          clearFormatterCache();
          setTimezone(fetchedTz);
        }
        
        setProfileData(data.data);
        ProfileCache.set(user_at, data.data);
      }
    } catch (error) {
      console.error('Error fetching profile details:', error);
      setProfileError('Unable to load profile. Please check your connection and try again.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleProfileUpdate = (newImageUrl) => {
    const updatedProfile = { 
      ...profileData, 
      profile_image_url: newImageUrl
    };
    setProfileData(updatedProfile);
    ProfileCache.set(user_at, updatedProfile);
  };

  const handleLogout = async () => {
    try {
      const response = await axios.get('/pceapi/auth/iamuser/logout', {
        headers: { Authorization: accessToken }
      });
      if (response.data.status === 'success') {
        store.dispatch({ type: 'RESET_ALL_STATE' });
        ProfileCache.clear(user_at);
        NavStateCache.clear(user_at);
        clearCookie('access_token');
        navigate('/login');
      } else {
        console.error('Logout failed:', response.data.message);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg fixed-top topnavbar col-lg-10 col-md-9 col-sm-9">
        <div className="container-fluid ps-0">
          <div className="justify-content-center">
            <AiSearchComponent 
              tok={accessToken} 
              where_user={user_at} 
              ai_response={response_ai} 
              setResponse={set_response_ai} 
              isLoading={isLoading} 
              setIsLoading={setIsLoading} 
              setError={setError} 
            />
          </div>
          <div className="">
            <ThemeToggle />

            <button className="ev-btn-blue-fill-round ev-btn-med ev-bold-reg ms-3" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right"></i> Logout 
            </button>

            <div className="d-inline-block position-relative ms-3" ref={profileButtonRef}>
              {isProfileLoading ? (
                <button className="ev-btn-lightblue-fill-round nav-round-btn">
                  <i className="bi bi-person-fill"></i>
                </button>
              ) : profileData?.profile_image_url ? (
                <button
                  className="avatar-tooltip bg-transparent border-0"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <img 
                    src={profileData.profile_image_url} 
                    className="nav-profile-image"
                    onLoad={(e) => e.target.classList.add('loaded')}
                  />
                </button>
              ) : (
                <button
                  className="ev-btn-lightblue-fill-round nav-round-btn avatar-tooltip"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <i className="bi bi-person-fill"></i>
                </button>
              )}
              <ProfilePopup
                profileData={profileData}
                consoleType={user_at}
                onClose={() => setShowProfilePopup(false)}
                onProfileUpdate={handleProfileUpdate}
                isOpen={showProfilePopup}
              />
            </div>
            
            <Link 
              to="/iam-user-guide"
              className="ev-btn-lightblue-fill-round nav-round-btn ms-3 avatar-tooltip text-decoration-none d-inline-flex align-items-center justify-content-center"
              data-tooltip="User Help Guide"
            >
              <i className="bi bi-patch-question-fill"></i>
            </Link>

            <NotificationDropdown user_type={"IAM"} />
          </div>
        </div>
      </nav>

      {(isLoading || response_ai !== '' && error === null) && (
        <SerachDisplayDiv
          ai_response={response_ai}
          set_ai_response={set_response_ai}
          error={error}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setError={setError}
          clearQuestion={() => set_response_ai('')}
        />
      )}
      
      {error !== null && error !== '' && (
        <ErrorPopup
          onClose={() => setError(null)}
          message={error}
        />
      )}

      {profileError && (
        <ErrorPopup
          onClose={() => {
            setProfileError(null);
            fetchProfileDetails();
          }}
          message={profileError}
        />
      )}
    </>
  );
};

export const SideNav = () => {
  const location = useLocation();
  const consoleType = 'iam-console';
  const navigate = useNavigate();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });

  useEffect(() => {
    if (sidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed);
  }, [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const getInitialNavState = () => {
    const cachedState = NavStateCache.get(consoleType);
    if (cachedState) {
      return cachedState;
    }
    return {
      modules: true,
      tools: false,
      slr: false,
      slr_tools: true,
    };
  };

  const [expanded, setExpanded] = useState(getInitialNavState());

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleClick = (event, path) => {
    event.preventDefault();
    authNavigate(path, 'iam', navigate);
  };

  const toggleExpand = (branch) => {
    const newExpandedState = {
      ...expanded,
      [branch]: !expanded[branch]
    };
    setExpanded(newExpandedState);
    NavStateCache.set(consoleType, newExpandedState);
  };

  return (
    <nav className="col-lg-2 col-md-3 col-sm-3 sidebar">
      <div className="position-sticky">
        {/* Logo + toggle on same line */}
        <div className="sidebar-header-row">
          {/* Full logo — shown only when expanded */}
          {!sidebarCollapsed && (
            <Link to="/screening-space" className="text-decoration-none sidebar-logo-link">
              <img src="/media/pharmacoevidence-logo.png" alt="PharmacoEvidence" className="logo-color sidebar-logo" />
            </Link>
          )}

          {/* Scaled down logo — fits fully within collapsed sidebar width */}
          {sidebarCollapsed && (
            <Link to="/screening-space" className="text-decoration-none mb-1" style={{ display: 'block', width: 'calc(var(--px-65) - var(--px-12))', flexShrink: 0, overflow: 'hidden' }}>
              <img
                src="/media/pharmacoevidence-logo.png"
                className="logo-color"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Link>
          )}

          <button
            className="ev-btn-white-fill-round ev-icon-btn-padding-small"
            title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            onClick={toggleSidebar}
          >
            <i className={`bi ${sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        </div>

        {/* Collapsed icon nav — only icons, visible when collapsed */}
        {sidebarCollapsed && (
          <div className="sidebar-collapsed-icons">
            <Link className={`sidebar-icon-link ${isActive('/unstructured-threads')}`} onClick={(e) => handleClick(e, '/unstructured-threads')}>
              <i className="bi bi-bounding-box-circles"></i>
              <span className="sidebar-icon-label">Data Manager</span>
            </Link>
            <Link className={`sidebar-icon-link ${isActive('/teams')}`} onClick={(e) => handleClick(e, '/teams')}>
              <i className="bi bi-people"></i>
              <span className="sidebar-icon-label">Manage Teams</span>
            </Link>
            <div className="sidebar-icon-divider"></div>
            {/* <Link className={`sidebar-icon-link ${isActive('/genie.ai')}`} onClick={(e) => handleClick(e, '/genie.ai')}>
              <i className="bi bi-asterisk"></i>
              <span className="sidebar-icon-label">Genie.ai</span>
            </Link> */}
            {/* <Link className={`sidebar-icon-link ${isActive('/textractor')}`} onClick={(e) => handleClick(e, '/textractor')}>
              <i className="bi bi-bricks"></i>
              <span className="sidebar-icon-label">Textractor</span>
            </Link> */}
            {/* <Link className={`sidebar-icon-link ${isActive('/genbi')}`} onClick={(e) => handleClick(e, '/genbi')}>
              <i className="bi bi-kanban"></i>
              <span className="sidebar-icon-label">GenBI Studio</span>
            </Link> */}
           {/* <Link className={`sidebar-icon-link ${isActive('/genbook-playground')}`} onClick={(e) => handleClick(e, '/genbook-playground')}>
              <i className="bi bi-file-spreadsheet"></i>
              <span className="sidebar-icon-label">Genbook <span className="ev-badge-green-gradient" style={{fontSize: 'var(--px-8)'}}>Excel Classic</span></span>
            </Link> */}
            {/* <Link className={`sidebar-icon-link ${isActive('/airspace')}`} onClick={(e) => handleClick(e, '/airspace')}>
              <i className="bi bi-snow"></i>
              <span className="sidebar-icon-label">Airspace</span>
            </Link> */}
            {/* <Link className={`sidebar-icon-link ${isActive('/ask-sara')}`} onClick={(e) => handleClick(e, '/ask-sara')}>
              <i className="bi bi-chat-dots"></i>
              <span className="sidebar-icon-label">Ask Sara</span>
            </Link> */}
            {/* <Link className={`sidebar-icon-link ${isActive('/nexus')}`} onClick={(e) => handleClick(e, '/nexus')}>
              <i className="bi bi-diagram-3"></i>
              <span className="sidebar-icon-label">Nexus</span>
            </Link> */}
            <Link className={`sidebar-icon-link ${isActive('/screening-space')}`} onClick={(e) => handleClick(e, '/screening-space')}>
              <i className="bi bi-list-task"></i>
              <span className="sidebar-icon-label">Screening</span>
            </Link>
            <Link className={`sidebar-icon-link ${isActive('/genai-rights')}`} onClick={(e) => handleClick(e, '/genai-rights')}>
              <i className="bi bi-shield-check"></i>
              <span className="sidebar-icon-label">GenAI Rights</span>
            </Link>
          </div>
        )}

        {/* Nav links — scrollable so a long menu scrolls instead of
            overflowing the fixed-position sidebar and hiding the footer */}
        {/* .sidelink's own CSS fixes it at 95% width regardless of this
            container's padding, which produced its own separate gap.
            Scoped override so every sidelink here fills the padded box
            instead, leaving exactly one controlled gap before the
            scrollbar. */}
        {!sidebarCollapsed && (<>
        <style>{`
            .sidebar-nav-links .sidelink { width: 100% !important; }
        `}</style>
        <div className="sidebar-nav-links scrollable-element vh-90 pe-4"
            style={{ overflowY: "auto", overflowX: "hidden" }}>
            <div className="nav-branch">
              <p
                className="ev-small-p-dark mb-0 mt-4"
                onClick={() => toggleExpand('modules')}
              >
                <i className={`bi ${expanded.modules ? 'bi-dash-square-dotted' : 'bi-plus-square-dotted'} tree-chevron`}></i>
                &nbsp; Data & Team Management
              </p>

              {expanded.modules && (
                <div className="branch-content">
                  <Link className={`sidelink ${isActive('/unstructured-threads')}`} onClick={(e) => handleClick(e, '/unstructured-threads')}>
                    <i className="bi bi-bounding-box-circles"></i> &nbsp; Data Manager
                  </Link>
                  <Link className={`sidelink ${isActive('/teams')}`} onClick={(e) => handleClick(e, '/teams')}>
                    <i className="bi bi-people"></i> &nbsp; Manage Teams
                  </Link>
                </div>
              )}
            </div>

            {/* <div className="nav-branch">
              <p
                className="ev-small-p-dark mb-0 mt-3"
                onClick={() => toggleExpand('tools')}
              >
                <i className={`bi ${expanded.tools ? 'bi-dash-square-dotted' : 'bi-plus-square-dotted'} tree-chevron`}></i>
                &nbsp; <span className="ev-btn-gradient-fill-round pt-1 pb-1 ev-fs-11">Build HEOR AI Workflows</span>
              </p>

              {expanded.tools && (
                <div className="branch-content">
                  <Link className={`sidelink ${isActive('/genie.ai')}`} onClick={(e) => handleClick(e, '/genie.ai')}>
                    <i className="bi bi-asterisk"></i> &nbsp; Genie.ai
                  </Link>
                  <Link className={`sidelink ${isActive('/textractor')}`} onClick={(e) => handleClick(e, '/textractor')}>
                    <i className="bi bi-bricks"></i> &nbsp; Textractor
                  </Link>
                  <Link className={`sidelink ${isActive('/genbi')}`} onClick={(e) => handleClick(e, '/genbi')}>
                    <i className="bi bi-kanban"></i> &nbsp; GenBI Studio
                  </Link>
                  <Link className={`sidelink ${isActive('/genbook-playground')}`} style={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 'var(--px-5)'}} onClick={(e) => handleClick(e, '/genbook-playground')}>
                    <i className="bi bi-file-spreadsheet"></i> Genbook
                    <span className="ev-badge-green-gradient">Excel Classic</span>
                    <span
                      className="ev-badge-green-gradient"
                      style={{background: 'var(--color10)', color: 'var(--color2)', cursor: 'pointer'}}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/genie.ai', { state: { excelPro: true } }); }}
                    >
                      Try Pro <i className="bi bi-box-arrow-up-right"></i>
                    </span>
                  </Link>
                  <Link className={`sidelink ${isActive('/airspace')}`} onClick={(e) => handleClick(e, '/airspace')}>
                    <i className="bi bi-snow"></i> &nbsp; Airspace
                  </Link>
                  <Link className={`sidelink ${isActive('/ask-sara')}`} onClick={(e) => handleClick(e, '/ask-sara')}>
                    <i className="bi bi-chat-dots"></i> &nbsp; Ask Sara
                  </Link>
                  <Link className={`sidelink ${isActive('/nexus')}`} style={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 'var(--px-5)'}} onClick={(e) => handleClick(e, '/nexus')}>
                    <i className="bi bi-diagram-3"></i> Nexus
                    <span className="ev-badge-purple-gradient">ITC/NMA</span>
                  </Link>
                </div>
              )}
            </div> */}

            <div className="nav-branch">
              <p
                className="ev-small-p-dark mb-0 mt-3"
                onClick={() => toggleExpand('slr_tools')}
              >
                <i className={`bi ${expanded.slr_tools ? 'bi-dash-square-dotted' : 'bi-plus-square-dotted'} tree-chevron`}></i>
                &nbsp; SLR Tools
              </p>

              {expanded.slr_tools && (
                <div className="branch-content">
                  {/* <Link className={`sidelink ${isActive('/literature-discovery')}`} onClick={(e) => handleClick(e, '/literature-discovery')}>
                    <i className="bi bi-search"></i> &nbsp; Literature Discovery
                  </Link> */}
                  <Link className={`sidelink ${isActive('/screening-space')}`} onClick={(e) => handleClick(e, '/screening-space')}>
                    <i className="bi bi-list-task"></i> &nbsp; Screening Space
                  </Link>
                  <Link className={`sidelink ${isActive('/genai-rights')}`} onClick={(e) => handleClick(e, '/genai-rights')}>
                    <i className="bi bi-shield-check"></i> &nbsp; GenAI Rights
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>)}
        </div>
        <Footer />
    </nav>
  );
};

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}